import { styled, Text, useTranslation } from '@obvio/app'
import { SvgBin, SvgEdit } from '@obvio/svg'
import { Button, Grid, Stack } from '@obvio/ui'
import { capitalize } from '@obvio/utils'
import { useState } from 'react'

import { FormArtworkInner } from './FormArtworkInner'
import { useArtworksContext } from './FormArtworksContext'

import type { ImageAsset } from '@obvio/app'
import type { ReactElement } from 'react'

const Wrap = styled(Grid)`
  border: 1px solid ${(theme) => theme.colors.additional[2]};
  width: 100%;
`

const Bar = styled.div`
  display: flex;
  background-color: ${(theme) => theme.colors.additional[2]};
  padding: ${(theme) => theme.spacing.extraSmall}
    ${(theme) => theme.spacing.small};
  justify-content: space-between;
  button {
    height: auto !important;
    width: auto !important;
  }
  svg {
    height: 1.25rem !important;
  }
`

const Content = styled(Grid)`
  padding: ${(theme) => theme.spacing.medium};
  height: 11rem;
  text-align: left;
  p {
    font-size: 0.8125rem;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const Image = styled.img`
  width: 100%;
  height: 100%;
`

export type Artwork = {
  title: string
  description: string
  // TODO: urlData is  already in ImageAsset, app is not building on vercel for some reason
  images: (ImageAsset & { urlData?: string })[]
  category: string
  size: string
  rarity: string
  year: number
}

type FormArtworkProps = Artwork & {
  idx: number
  id: string
  close(): void
}

export function FormArtwork({
  idx,
  title,
  images,
  description,
  close,
  id,
  ...rest
}: FormArtworkProps): ReactElement {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<boolean>(false)
  const { removeArtwork } = useArtworksContext()

  if (editing) {
    return (
      <FormArtworkInner save={() => setEditing(false)} close={close} id={id} />
    )
  }

  return (
    <Wrap>
      <Bar>
        <Text tag="span">
          {t('event.artwork')} {idx}
        </Text>
        <Stack>
          <Button
            kind={['icon', 'transparent']}
            onClick={() => setEditing(true)}
          >
            <SvgEdit />
          </Button>
          <Button
            kind={['icon', 'transparent']}
            onClick={() => removeArtwork(id)}
          >
            <SvgBin />
          </Button>
        </Stack>
      </Bar>
      <Content templateColumns="1fr 1fr 1fr" gap="extraLarge">
        <Image src={images[0]?.urlData} alt={images[0].name} />
        <Column>
          <Text>
            {t('label.title')}: {title}
          </Text>
          <Text>
            {t('label.description')}: {description}
          </Text>
        </Column>
        <Column>
          {Object.entries(rest).map(([key, value]) => (
            <Text key={key}>
              {capitalize(key)}:{' '}
              {typeof value === 'string' || typeof value === 'number'
                ? String(value).toUpperCase()
                : ''}
            </Text>
          ))}
        </Column>
      </Content>
    </Wrap>
  )
}
